import { useSelector as useSelectorOriginal } from "store/useSelector";
import { useAuth0 } from "../../../../../../contexts/AuthManager/Auth";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AUTH_PROFILE_NAME } from "constants/constants";
import { SERVER_NAMES } from "../SERVER_NAMES";
import {
  DataDumpAggregateType,
  ManyRollupsWithTagsAndRange,
} from "types/types";
import { mapObjectValues } from "utils/arrayUtils";
import { objectFromEntries } from "@raven/shared/utils";
import {
  NetworkRequest,
  useInterceptedNetworkRequests,
} from "../useInterceptedNetworkRequests";
import { useDataDumpRows } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { DocumentNode } from "graphql";
import {
  ALLOCATION_LIST_QUERY,
  BATCH_PROGRESSION_QUERY,
  DATA_DUMP_AGGREGATE_QUERY,
  DATA_DUMP_ROWS_QUERY,
} from "providers/MidgardProvider";
import { isEqual, throttle, uniq } from "lodash";
import {
  PRODUCTION_SEGMENTS_QUERY,
  SHIFT_SEGMENTS_QUERY,
} from "providers/AskrProvider";
import { getEndpointUrl } from "./getEndpointUrl";
import {
  AGGREGATE_NO_FREQUENCY_QUERY,
  AGGREGATE_ON_FREQUENCY_QUERY,
} from "segments/AggregatesGetter/aggregatesGetterUtils";
import { PROCESS_GROUPS_QUERY } from "components/DataGetter/ProcessGroupsGetter";
import {
  useFilteredProcessIds,
  useIsSingleProcessPage,
  useTrendChartBinSize,
  useVisibleProcessIds,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { TRENDS_BIN_SIZE_VALUES_MS_MAP } from "utils/timeUtils";
import { useCurrentAskrSegment } from "components/DataGetter/CurrentSegmentGetter";
import { useMemo } from "react";
import { ProductionSegment } from "reports/types";

const DATA_DUMPS = "data dumps";

/** network info split by servers and resources
 * e.g. Askr > shifts, segments, aggregates...
 */
export type NetworkConnectionsType = {
  [serverTitle: string]: NetworkConnectionType;
};
export type NetworkConnectionType = {
  networkMetadata?: NetworkMetadataType;
  resources: NetworkResourcesType;
};

export type NetworkMetadataType = {
  requests: NetworkRequest[];
  timesUpdated: number;
  isGraphqlPlayground?: boolean;
  graphiqlUrl?: string;
};
export type NetworkResourcesType = {
  [networkName: string]: {
    isNetworkOnline: boolean;
    data: any;
    timesUpdated: number;
    itemCounts: { itemName: string; itemCount: number }[];
    serverName: keyof typeof SERVER_NAMES;
    graphiqlUrlOrUrls?: string | { name: string; url: string }[];
  };
};

/** since debugger is very expensive to render, throttle update frequency */
const useSelector = (selectorFn, equalityFn = undefined) =>
  useSelectorOriginal(throttle(selectorFn, 2000), equalityFn);

export function useDebuggerGroupsAuth(): NetworkConnectionType {
  const authContext = useAuth0();

  return useMemo(
    () =>
      ({
        Auth: {
          resources: {
            [AUTH_PROFILE_NAME]: {
              isNetworkOnline: authContext.isAuthenticated,
              data: authContext,
              timesUpdated: 0,
              itemCounts: [] as any[],
              serverName: SERVER_NAMES.auth0,
            },
          },
        },
      })["Auth"] as unknown as NetworkConnectionType,
    [authContext]
  );
}
export function useDebuggerGroupsLD(): NetworkConnectionType {
  // LaunchDarkly flags
  // don't use useFlagsWithDefaults here so we can assess actual "undefined" vs "false"
  // to tell if the data has arrived yet
  const flags = useFlags();

  return useMemo(
    () =>
      ({
        LaunchDarkly: {
          resources: {
            flags: {
              // if any value is defined, LaunchDarkly is online
              isNetworkOnline: Object.values(flags).some(
                value => value !== undefined
              ),
              data: flags,
              itemCounts: [
                {
                  itemName: "flags active",
                  itemCount: Object.values(flags).reduce((acc, cur) => {
                    const isFlagActive =
                      cur === true ||
                      isEqual(cur, []) ||
                      (typeof cur === "string" && cur.length > 0);
                    return acc + (isFlagActive ? 1 : 0);
                  }, 0),
                },
              ],
              timesUpdated: 0,
              serverName: SERVER_NAMES.launchDarkly,
            },
          },
        },
      })["LaunchDarkly"] as unknown as NetworkConnectionType,
    [flags]
  );
}
export function useDebuggerGroupsMidgard(): NetworkConnectionType {
  // Midgard process groups
  const visibleGroups = useSelector(state => state.legacy.visibleGroups);
  const allGroups = useSelector(state => state.legacy.allGroups);
  const lastDataDumpRow = useSelector(state => state.legacy.lastDataDumpRow);
  const lastDataDumpRowPerProcess = useSelector(
    state => state.legacy.lastDataDumpRowPerProcess
  );

  // Askr shifts, segments, aggregates

  const stateDataDumpsTrendChart = useSelector(
    state => state.legacy.dataDumpsTrendChart
  );
  const dataDumpsTrendChart = useMemo(
    () =>
      stateDataDumpsTrendChart
        .sort((a, b) => a.range.ge - b.range.ge)
        .map(addReadableDateRangeToObject) as DataDumpAggregateType[],
    [stateDataDumpsTrendChart]
  );
  const stateDataDumpsTrendChartCounts = useSelector(
    state => state.legacy.dataDumpsTrendChartCounts
  );
  const dataDumpsTrendChartCounts = useMemo(
    () =>
      stateDataDumpsTrendChartCounts
        .sort((a, b) => a.range.ge - b.range.ge)
        .map(addReadableDateRangeToObject) as DataDumpAggregateType[],
    [stateDataDumpsTrendChartCounts]
  );
  const dataDumpFullRangeWithoutReadableDates = useSelector(
    state => state.legacy.dataDumpFullRange
  );
  const dataDumpFullRange = useMemo(
    () => addReadableDateRangeToObject(dataDumpFullRangeWithoutReadableDates),
    [dataDumpFullRangeWithoutReadableDates]
  ) as DataDumpAggregateType | null;

  const dataDumpHubletViewWithoutReadableDates = useSelector(
    state => state.legacy.dataDumpHubletView
  );
  const dataDumpHubletView = useMemo(
    () => addReadableDateRangeToObject(dataDumpHubletViewWithoutReadableDates),
    [dataDumpHubletViewWithoutReadableDates]
  ) as DataDumpAggregateType | null;

  const stateDataDumpsFullRangeOverrideMachemetryIds = useSelector(
    state => state.legacy.dataDumpsFullRangeOverrideMachemetryIds
  );
  const dataDumpsFullRangeOverrideMachemetryIds = useMemo(
    () =>
      mapObjectValues(
        stateDataDumpsFullRangeOverrideMachemetryIds,
        addReadableDateRangeToObject
      ) as {
        [processId: string]: DataDumpAggregateType;
      },
    [stateDataDumpsFullRangeOverrideMachemetryIds]
  );

  const dataDumpSinceLotStartWithoutReadableDates = useSelector(
    state => state.legacy.dataDumpSinceLotStart
  );
  const dataDumpSinceLotStart = useMemo(
    () =>
      addReadableDateRangeToObject(
        dataDumpSinceLotStartWithoutReadableDates
      ) as DataDumpAggregateType | null,
    [dataDumpSinceLotStartWithoutReadableDates]
  );

  const dataDumpHubletViewRollingWindowWithoutReadableDates = useSelector(
    state => state.legacy.dataDumpHubletViewRollingWindow
  );
  const dataDumpHubletViewRollingWindow = useMemo(
    () =>
      addReadableDateRangeToObject(
        dataDumpHubletViewRollingWindowWithoutReadableDates
      ) as DataDumpAggregateType | null,
    [dataDumpHubletViewRollingWindowWithoutReadableDates]
  );

  const dataDumpFullRangeSubordinateProcessesWithoutReadableDate = useSelector(
    state => state.legacy.dataDumpFullRangeSubordinateProcesses
  );
  const dataDumpFullRangeSubordinateProcesses = useMemo(
    () =>
      addReadableDateRangeToObject(
        dataDumpFullRangeSubordinateProcessesWithoutReadableDate
      ) as DataDumpAggregateType | null,
    [dataDumpFullRangeSubordinateProcessesWithoutReadableDate]
  );
  const filteredProcessIdsWithLeaderConstraintFilter =
    useFilteredProcessIds(true);
  const filteredProcessIds = useFilteredProcessIds(false);
  const [dataDumpRows] = useDataDumpRows();
  // Midgard lot counts
  const allLotInfo = useSelector(state => state.legacy.allLotInfo);
  const allLotCounts = useSelector(state => state.legacy.allLotCounts);

  const customerInfoJson = useSelector(state => state.legacy.customerJson);

  // Midgard allocation list
  const allocationList = useSelector(state => state.legacy.allocationList);

  const allTimesUpdated = useSelector(state => state.legacy.timesUpdated);

  // LaunchDarkly flags
  // don't use useFlagsWithDefaults here so we can assess actual "undefined" vs "false"
  // to tell if the data has arrived yet

  const { midgardRequests } = useInterceptedNetworkRequests();
  const ckey = useCustomerKey();
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();

  const stateDataDumpsPnlLossesByUnit = useSelector(
    state => state.legacy.dataDumpsPnlLossesByUnit
  );
  // Midgard data dumps
  const dataDumpsPnlLossesByUnit = useMemo(
    () =>
      mapObjectValues(
        stateDataDumpsPnlLossesByUnit,
        addReadableDateRangeToObject
      ) as {
        [key: string]: DataDumpAggregateType;
      },
    [stateDataDumpsPnlLossesByUnit]
  );
  const stateDataDumpsGroupTiles = useSelector(
    state => state.legacy.dataDumpsGroupTiles
  );
  const dataDumpsGroupTiles = useMemo(
    () =>
      mapObjectValues(
        stateDataDumpsGroupTiles,
        addReadableDateRangeToObject
      ) as {
        [key: string]: DataDumpAggregateType;
      },
    [stateDataDumpsGroupTiles]
  );
  const stateDataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const dataDumpsFullRangeByProcess = useMemo(
    () =>
      mapObjectValues(
        stateDataDumpsFullRangeByProcess,
        addReadableDateRangeToObject
      ) as {
        [processId: string]: DataDumpAggregateType;
      },
    [stateDataDumpsFullRangeByProcess]
  );

  const { midgardGraphiqlUrl } = useMemo(() => {
    const { Askr, Midgard } = getEndpointUrl(ckey);
    const a = Askr ? `${Askr}/graphiql` : "";
    const m = Midgard ? `${Midgard}/graphiql` : "";
    return { askrGraphiqlUrl: a, midgardGraphiqlUrl: m };
  }, [ckey]);
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const isSingleProcessPage = useIsSingleProcessPage();
  const visibleProcessIds = useVisibleProcessIds();

  const visibleGroupsWithProcessIdsToNamesMap = useMemo(
    () => ({
      processIdsToNamesMap: visibleGroups.reduce((acc, group) => {
        return {
          ...acc,
          [group.longName]: group.processes.reduce((acc2, process) => {
            return { ...acc2, [process.id]: process.name };
          }, {}),
        };
      }, {}),
      visibleGroups,
      allGroups,
    }),
    [visibleGroups, allGroups]
  );
  const selectedWorkSchedule = useMemo(
    () =>
      workScheduleConfigList.find(
        workScheduleConfig => workScheduleConfig.id === workScheduleId
      ),
    [workScheduleConfigList, workScheduleId]
  );
  return useMemo(
    () =>
      ({
        Midgard: {
          networkMetadata: {
            requests: midgardRequests,
            timesUpdated:
              allTimesUpdated.midgardDataDumpsTrendChartData +
              allTimesUpdated.midgardDataDumpsTrendChartCountsData +
              allTimesUpdated.midgardDataDumpFullRangeData +
              allTimesUpdated.midgardDataDumpsPnlLossesByUnitData +
              allTimesUpdated.midgardDataDumpsFullRangeByProcess +
              allTimesUpdated.midgardDataDumpHubletViewData +
              allTimesUpdated.midgardDataDumpSinceLotStartData +
              allTimesUpdated.midgardDataDumpRollingWindowData +
              allTimesUpdated.midgardDataDumpsGroupTilesData +
              allTimesUpdated.midgardLotInfo +
              allTimesUpdated.midgardLotCounts +
              allTimesUpdated.supervisorAllocation +
              allTimesUpdated.midgardLastDataDumpRow,
            graphiqlUrl: midgardGraphiqlUrl,
          },
          resources: {
            "customer info": {
              isNetworkOnline: Boolean(customerInfoJson),
              data: { customerInfoJson },
              timesUpdated: 0,
              itemCounts: [
                {
                  itemName: "customer info",
                  itemCount: customerInfoJson ? 1 : 0,
                },
              ],
              serverName: SERVER_NAMES.midgardCustomerInfo,
              graphiqlUrlOrUrls: "",
            },
            "work schedules": {
              isNetworkOnline: Boolean(workScheduleConfigList),
              data: {
                schedules: workScheduleConfigList,
                selectedSchedule: selectedWorkSchedule,
              },
              timesUpdated: 0,
              itemCounts: [
                {
                  itemName: `schedule${
                    workScheduleConfigList.length === 1 ? "" : "s"
                  }`,
                  itemCount: workScheduleConfigList.length,
                },
                {
                  itemName: `selected schedule`,
                  itemCount: selectedWorkSchedule ? 1 : 0,
                },
              ],
              serverName: SERVER_NAMES.midgardWorkSchedules,
              graphiqlUrlOrUrls: "",
            },
            "process groups + ids-to-names map": {
              isNetworkOnline: Boolean(allGroups.length),
              data: visibleGroupsWithProcessIdsToNamesMap,
              itemCounts: [
                {
                  itemName: "allGroups",
                  itemCount: allGroups.length,
                },
                {
                  itemName: "visibleGroups",
                  itemCount: visibleGroups.length,
                },
                {
                  itemName: "processes",
                  itemCount: visibleGroups.reduce(
                    (acc, cur) => acc + cur.processes.length,
                    0
                  ),
                },
                {
                  itemName: "filtered",
                  itemCount: filteredProcessIds.length,
                },
                {
                  itemName: "leader/constraint",
                  itemCount:
                    filteredProcessIdsWithLeaderConstraintFilter.length,
                },
              ],
              timesUpdated: allTimesUpdated.midgardProcessGroups,
              serverName: SERVER_NAMES.midgardProcessGroups,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(PROCESS_GROUPS_QUERY),
                variables: {},
              }),
            },
            "trend chart DDAs": {
              isNetworkOnline: isNonEmptyArray(dataDumpsTrendChart),
              data: dataDumpsTrendChart,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: dataDumpsTrendChart.length,
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpsTrendChartData,
              serverName: SERVER_NAMES.midgardDataDumpsTrendChart,
              graphiqlUrlOrUrls: dataDumpsTrendChart.map((dd, idx) => ({
                name: `bar ${idx + 1}`,
                url: getGqlUrl({
                  base: midgardGraphiqlUrl,
                  queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                  variables: {
                    ge: String(dd?.range?.ge),
                    lt: String(dd?.range?.lt),
                    machemetryIds: dd?.processIds,
                  },
                }),
              })),
            },
            "trend chart counts DDAs": {
              isNetworkOnline: isNonEmptyArray(dataDumpsTrendChartCounts),
              data: dataDumpsTrendChartCounts,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: dataDumpsTrendChartCounts.length,
                },
              ],
              timesUpdated:
                allTimesUpdated.midgardDataDumpsTrendChartCountsData,
              serverName: SERVER_NAMES.midgardDataDumpsTrendChartCounts,
              graphiqlUrlOrUrls: dataDumpsTrendChartCounts.map((dd, idx) => ({
                name: `bar ${idx + 1}`,
                url: getGqlUrl({
                  base: midgardGraphiqlUrl,
                  queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                  variables: {
                    ge: String(dd?.range?.ge),
                    lt: String(dd?.range?.lt),
                    machemetryIds: dd?.processIds,
                  },
                }),
              })),
            },
            "full-range DDA": {
              isNetworkOnline: isNonEmptyObject(dataDumpFullRange),
              data: dataDumpFullRange,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: dataDumpFullRange ? 1 : 0,
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpFullRangeData,
              serverName: SERVER_NAMES.midgardDataDumpFullRange,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpFullRange?.range?.ge),
                  lt: String(dataDumpFullRange?.range?.lt),
                  machemetryIds: dataDumpFullRange?.processIds,
                },
              }),
            },
            "full-range DDAs by unit": {
              isNetworkOnline: isNonEmptyObject(dataDumpsPnlLossesByUnit),
              data: dataDumpsPnlLossesByUnit,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: numKeys(dataDumpsPnlLossesByUnit),
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpsPnlLossesByUnitData,
              serverName: SERVER_NAMES.midgardDataDumpsPnlLossesByUnit,
              graphiqlUrlOrUrls: Object.entries(dataDumpsPnlLossesByUnit).map(
                ([, dd]) => ({
                  name: dd.processNames.join(","),
                  url: getGqlUrl({
                    base: midgardGraphiqlUrl,
                    queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                    variables: {
                      ge: String(dd?.range?.ge),
                      lt: String(dd?.range?.lt),
                      machemetryIds: dd?.processIds,
                    },
                  }),
                })
              ),
            },
            "full-range DDAs by process": {
              isNetworkOnline: isNonEmptyObject(dataDumpsFullRangeByProcess),
              data: dataDumpsFullRangeByProcess,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: numKeys(dataDumpsFullRangeByProcess),
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpsFullRangeByProcess,
              serverName: SERVER_NAMES.midgardDataDumpsFullRangeByProcess,
              graphiqlUrlOrUrls: Object.entries(
                dataDumpsFullRangeByProcess
              ).map(([, dd]) => ({
                name: dd.processNames.join(","),
                url: getGqlUrl({
                  base: midgardGraphiqlUrl,
                  queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                  variables: {
                    ge: String(dd?.range?.ge),
                    lt: String(dd?.range?.lt),
                    machemetryIds: Object.keys(dataDumpsFullRangeByProcess),
                  },
                }),
              })),
            },
            "hublet view DDA": {
              isNetworkOnline: isNonEmptyObject(dataDumpHubletView),
              data: dataDumpHubletView,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: dataDumpHubletView ? 1 : 0,
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpHubletViewData,
              serverName: SERVER_NAMES.midgardDataDumpHubletView,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpHubletView?.range?.ge),
                  lt: String(dataDumpHubletView?.range?.lt),
                  machemetryIds: dataDumpHubletView?.processIds ?? [],
                },
              }),
            },
            "overridden metric DDAs": {
              isNetworkOnline: isNonEmptyObject(
                dataDumpsFullRangeOverrideMachemetryIds
              ),
              data: dataDumpsFullRangeOverrideMachemetryIds,
              itemCounts: [],
              timesUpdated: 0,
              serverName: SERVER_NAMES.midgardDataDumpOverriddenMetrics,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(
                    dataDumpsFullRangeOverrideMachemetryIds?.[0]?.range?.ge
                  ),
                  lt: String(
                    dataDumpsFullRangeOverrideMachemetryIds?.[0]?.range?.lt
                  ),
                  machemetryIds:
                    dataDumpsFullRangeOverrideMachemetryIds?.[0]?.processIds ??
                    [],
                },
              }),
            },
            "data-dump rows": {
              isNetworkOnline: isNonEmptyObject(dataDumpRows),
              data: objectFromEntries(
                Object.entries(dataDumpRows).map(([key, val]) => [
                  key,
                  objectFromEntries(
                    Object.entries(val).map(([key, val]) => [
                      `${key}—${val.pnlCategory}`,
                      val,
                    ])
                  ),
                ])
              ),
              itemCounts: [
                {
                  itemName: "row",
                  itemCount: Object.values(dataDumpRows).reduce(
                    (acc, cur) => acc + cur.length,
                    0
                  ),
                },
              ],
              timesUpdated: allTimesUpdated.midgardLastDataDumpRow,
              serverName: SERVER_NAMES.midgardDataDumpRows,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_ROWS_QUERY),
                variables: {
                  ge: String(visibleRange?.ge),
                  lt: String(visibleRange?.lt),
                  ckey,
                  machemetryIds: isSingleProcessPage
                    ? [visibleProcessIds[0]]
                    : visibleProcessIds,
                },
              }),
            },
            "last data-dump row": {
              isNetworkOnline: isNonEmptyObject(lastDataDumpRow),
              data: lastDataDumpRow,
              itemCounts: [
                {
                  itemName: "row",
                  itemCount: lastDataDumpRow ? 1 : 0,
                },
              ],
              timesUpdated: allTimesUpdated.midgardLastDataDumpRow,
              serverName: SERVER_NAMES.midgardLastDataDumpRow,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpHubletView?.range?.ge),
                  lt: String(dataDumpHubletView?.range?.lt),
                  machemetryIds: dataDumpHubletView?.processIds ?? [],
                },
              }),
            },
            "last data-dump row per process": {
              isNetworkOnline: isNonEmptyObject(lastDataDumpRowPerProcess),
              data: lastDataDumpRowPerProcess,
              itemCounts: [
                {
                  itemName: "rows",
                  itemCount: lastDataDumpRowPerProcess
                    ? Object.keys(lastDataDumpRowPerProcess).length
                    : 0,
                },
              ],
              timesUpdated: allTimesUpdated.midgardLastDataDumpRowPerProcess,
              serverName: SERVER_NAMES.midgardLastDataDumpRowPerProcess,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpHubletView?.range?.ge),
                  lt: String(dataDumpHubletView?.range?.lt),
                  machemetryIds: dataDumpHubletView?.processIds ?? [],
                },
              }),
            },
            "since-lot-start DDA": {
              isNetworkOnline: isNonEmptyObject(dataDumpSinceLotStart),
              data: dataDumpSinceLotStart,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: numKeys(dataDumpSinceLotStart),
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpSinceLotStartData,
              serverName: SERVER_NAMES.midgardDataDumpSinceLotStart,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpSinceLotStart?.range?.ge),
                  lt: String(dataDumpSinceLotStart?.range?.lt),
                  machemetryIds: dataDumpSinceLotStart?.processIds,
                },
              }),
            },
            "rolling window DDA": {
              isNetworkOnline: isNonEmptyObject(
                dataDumpHubletViewRollingWindow
              ),
              data: dataDumpHubletViewRollingWindow,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: numKeys(dataDumpHubletViewRollingWindow),
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpRollingWindowData,
              serverName: SERVER_NAMES.midgardDataDumpRollingWindow,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpHubletViewRollingWindow?.range?.ge),
                  lt: String(dataDumpHubletViewRollingWindow?.range?.lt),
                  machemetryIds: dataDumpHubletViewRollingWindow?.processIds,
                },
              }),
            },
            "group tiles DDAs": {
              isNetworkOnline: isNonEmptyObject(dataDumpsGroupTiles),
              data: dataDumpsGroupTiles,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: numKeys(dataDumpsGroupTiles),
                },
              ],
              timesUpdated: allTimesUpdated.midgardDataDumpsGroupTilesData,
              serverName: SERVER_NAMES.midgardDataDumpsGroupTiles,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(Object.values(dataDumpsGroupTiles)[0]?.range?.ge),
                  lt: String(Object.values(dataDumpsGroupTiles)[0]?.range?.lt),
                  machemetryIds:
                    Object.values(dataDumpsGroupTiles)[0]?.processIds,
                },
              }),
            },
            "subordinate processes DDAs": {
              isNetworkOnline: isNonEmptyObject(
                dataDumpFullRangeSubordinateProcesses as DataDumpAggregateType | null
              ),
              data: dataDumpFullRangeSubordinateProcesses,
              itemCounts: [
                {
                  itemName: DATA_DUMPS,
                  itemCount: dataDumpFullRangeSubordinateProcesses ? 1 : 0,
                },
              ],
              timesUpdated:
                allTimesUpdated.midgardDataDumpFullRangeSubordinateProcesses,
              serverName:
                SERVER_NAMES.midgardDataDumpFullRangeSubordinateProcesses,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: String(dataDumpFullRangeSubordinateProcesses?.range?.ge),
                  lt: String(dataDumpFullRangeSubordinateProcesses?.range?.lt),
                  machemetryIds:
                    dataDumpFullRangeSubordinateProcesses?.processIds,
                },
              }),
            },
            lotInfo: {
              isNetworkOnline: isNonEmptyObject(allLotInfo),
              data: allLotInfo,
              itemCounts: [
                {
                  itemName: "midgard lotInfo",
                  itemCount: numKeys(allLotInfo),
                },
              ],
              timesUpdated: allTimesUpdated.midgardLotInfo,
              serverName: SERVER_NAMES.midgardLotInfo,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(BATCH_PROGRESSION_QUERY),
                variables: {
                  ge: String(
                    (Object.values(allLotInfo)[0] as any)?.lotStartDateMs
                  ),
                  lt: String(Date.now()),
                  machemetryIds: Object.keys(allLotInfo),
                },
              }),
            },
            lotCounts: {
              isNetworkOnline: isNonEmptyObject(allLotCounts),
              data: allLotCounts,
              itemCounts: [
                {
                  itemName: "midgard lotCounts",
                  itemCount: numKeys(allLotCounts),
                },
              ],
              timesUpdated: allTimesUpdated.midgardLotCounts,
              serverName: SERVER_NAMES.midgardLotCounts,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(DATA_DUMP_AGGREGATE_QUERY),
                variables: {
                  ge: "",
                  lt: "",
                  machemetryIds: Object.keys(allLotCounts),
                },
              }),
            },
            "supervisor allocation": {
              isNetworkOnline: allocationList.length > 0,
              data: allocationList,
              itemCounts: [
                { itemName: "allocations", itemCount: allocationList.length },
              ],
              timesUpdated: allTimesUpdated.supervisorAllocation,
              serverName: SERVER_NAMES.supervisorAllocation,
              graphiqlUrlOrUrls: getGqlUrl({
                base: midgardGraphiqlUrl,
                queryBody: getQueryBody(ALLOCATION_LIST_QUERY),
                variables: {
                  ge: String(allocationList[0]?.range?.ge),
                  lt: String(allocationList[0]?.range?.lt),
                  machemetryIds: uniq(allocationList.map(a => a.processId)),
                },
              }),
            },
          },
        },
      })["Midgard"] as unknown as NetworkConnectionType,
    [
      visibleGroups,
      allGroups,
      lastDataDumpRow,
      lastDataDumpRowPerProcess,
      dataDumpsTrendChart,
      dataDumpsTrendChartCounts,
      dataDumpFullRange,
      dataDumpHubletView,
      dataDumpsFullRangeOverrideMachemetryIds,
      dataDumpSinceLotStart,
      dataDumpHubletViewRollingWindow,
      dataDumpFullRangeSubordinateProcesses,
      filteredProcessIdsWithLeaderConstraintFilter,
      filteredProcessIds,
      dataDumpRows,
      allLotInfo,
      allLotCounts,
      customerInfoJson,
      allocationList,
      allTimesUpdated,
      midgardRequests,
      ckey,
      workScheduleConfigList,
      dataDumpsPnlLossesByUnit,
      dataDumpsGroupTiles,
      dataDumpsFullRangeByProcess,
      midgardGraphiqlUrl,
      visibleRange,
      isSingleProcessPage,
      visibleProcessIds,
      visibleGroupsWithProcessIdsToNamesMap,
      selectedWorkSchedule,
    ]
  );
}

export function useDebuggerGroupsAskr(): NetworkConnectionType {
  // Midgard process groups

  // Askr shifts, segments, aggregates
  const shifts = useSelector(state => state.legacy.shifts);
  const segments = useSelector(state => state.legacy.segments);

  const [currentAskrSegment] = useCurrentAskrSegment();
  // Midgard lot counts

  // Midgard allocation list

  const allTimesUpdated = useSelector(state => state.legacy.timesUpdated);

  // LaunchDarkly flags
  // don't use useFlagsWithDefaults here so we can assess actual "undefined" vs "false"
  // to tell if the data has arrived yet

  const { askrRequests } = useInterceptedNetworkRequests();
  const ckey = useCustomerKey();

  const stateAllRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );
  const allRollupsMetricTrendChartBarsArrays = useMemo(
    () =>
      mapObjectValues(stateAllRollupsMetricTrendChartBarsArrays, trendBarsArr =>
        trendBarsArr.map(addReadableDateRangeToObject)
      ),
    [stateAllRollupsMetricTrendChartBarsArrays]
  );
  const stateRollupsMetricByProcess = useSelector(
    state => state.legacy.rollupsMetricByProcess
  );
  const rollupsMetricByProcess = useMemo(
    () =>
      mapObjectValues(
        stateRollupsMetricByProcess,
        addReadableDateRangeToObject
      ) as ManyRollupsWithTagsAndRange,
    [stateRollupsMetricByProcess]
  );
  // Midgard data dumps

  const { askrGraphiqlUrl } = useMemo(() => {
    const { Askr, Midgard } = getEndpointUrl(ckey);
    const a = Askr ? `${Askr}/graphiql` : "";
    const m = Midgard ? `${Midgard}/graphiql` : "";
    return { askrGraphiqlUrl: a, midgardGraphiqlUrl: m };
  }, [ckey]);

  const { trendChartBinSizeMs } = useTrendChartBinSize();
  return useMemo(
    () =>
      ({
        Askr: {
          networkMetadata: {
            requests: askrRequests,
            timesUpdated:
              allTimesUpdated.askrShiftsData +
              allTimesUpdated.askrSegmentsData +
              allTimesUpdated.askrAggregatesData,
            graphiqlUrl: askrGraphiqlUrl,
            isGraphqlPlayground: true,
          },
          resources: {
            shifts: {
              isNetworkOnline: isNonEmptyArray(shifts),
              data: shifts,
              itemCounts: [{ itemName: "shifts", itemCount: shifts.length }],
              timesUpdated: allTimesUpdated.askrShiftsData,
              serverName: SERVER_NAMES.askrShifts,
              graphiqlUrlOrUrls: getGqlUrl({
                base: askrGraphiqlUrl,
                queryBody: getQueryBody(SHIFT_SEGMENTS_QUERY).replace(
                  `@connection(key: "shiftSegments", filter: []) `,
                  ""
                ),
                variables: {
                  ge: String(shifts[0]?.range?.ge),
                  lt: String(shifts[shifts.length - 1]?.range?.lt),
                },
              }),
            },
            segments: {
              isNetworkOnline: isNonEmptyObject(segments),
              data: segments,
              itemCounts: [
                { itemName: "processes", itemCount: numKeys(segments) },
                {
                  itemName: "segments",
                  itemCount: Object.values(segments).reduce(
                    (acc, cur) =>
                      (acc as number) + (cur as ProductionSegment[]).length,
                    0
                  ),
                },
              ],
              timesUpdated: allTimesUpdated.askrSegmentsData,
              serverName: SERVER_NAMES.askrSegments,
              graphiqlUrlOrUrls: getGqlUrl({
                base: askrGraphiqlUrl,
                queryBody: getQueryBody(PRODUCTION_SEGMENTS_QUERY).replace(
                  `@connection(key: "productionSegments", filter: ["process"]) `,
                  ""
                ),
                variables: {
                  machemetrySourceName: Object.keys(segments)[0],
                  ge: String(Object.values(segments)[0]?.[0]?.range.ge),
                  lt: String(Object.values(segments)[0]?.[0]?.range.lt),
                },
              }),
            },
            currentSegment: {
              isNetworkOnline: Boolean(currentAskrSegment),
              data: segments,
              itemCounts: [
                {
                  itemName: "currentSegment",
                  itemCount: currentAskrSegment ? 1 : 0,
                },
              ],
              timesUpdated: allTimesUpdated.askrSegmentsData,
              serverName: SERVER_NAMES.askrCurrentSegment,
              graphiqlUrlOrUrls: getGqlUrl({
                base: askrGraphiqlUrl,
                queryBody: getQueryBody(PRODUCTION_SEGMENTS_QUERY).replace(
                  `@connection(key: "productionSegments", filter: ["process"]) `,
                  ""
                ),
                variables: {
                  machemetrySourceName: Object.keys(segments)[0],
                  ge: String(Object.values(segments)[0]?.[0]?.range.ge),
                  lt: String(Object.values(segments)[0]?.[0]?.range.lt),
                },
              }),
            },
            aggregates: {
              isNetworkOnline:
                isNonEmptyObject(allRollupsMetricTrendChartBarsArrays) ||
                isNonEmptyObject(rollupsMetricByProcess),
              data: {
                rollupsMetric: rollupsMetricByProcess,
                allRollupsMetricTrendChartBarsArrays,
              },
              itemCounts: [
                {
                  itemName: "fullRange",
                  itemCount: numKeys(rollupsMetricByProcess),
                },
                {
                  itemName: "trendChart",
                  itemCount: numKeys(allRollupsMetricTrendChartBarsArrays),
                },
                {
                  itemName: "trendChartBars",
                  itemCount: Object.entries(
                    allRollupsMetricTrendChartBarsArrays
                  ).reduce((acc, [, val]) => acc + (val as any).length, 0),
                },
              ],
              timesUpdated: allTimesUpdated.askrAggregatesData,
              serverName: SERVER_NAMES.askrAggregates,
              graphiqlUrlOrUrls: Object.entries(rollupsMetricByProcess).reduce(
                (acc, [machemetryId, aggregate]) => [
                  ...acc,
                  {
                    name: `${machemetryId} - fullRange`,
                    url: getGqlUrl({
                      base: askrGraphiqlUrl,
                      queryBody: getQueryBody(
                        AGGREGATE_NO_FREQUENCY_QUERY
                      ).replace(
                        `@connection(key: "productionAggregates", filter: ["process"]) `,
                        ""
                      ),
                      variables: {
                        machemetrySourceName: machemetryId,
                        ge: String(aggregate.range.ge),
                        lt: String(aggregate.range.lt),
                      },
                    }),
                  },
                  {
                    name: `${machemetryId} - trendChart`,
                    url: getGqlUrl({
                      base: askrGraphiqlUrl,
                      queryBody: getQueryBody(
                        AGGREGATE_ON_FREQUENCY_QUERY
                      ).replace(
                        `@connection(key: "productionAggregates", filter: ["process"]) `,
                        ""
                      ),
                      variables: {
                        machemetrySourceName: machemetryId,
                        ge: String(aggregate.range.ge),
                        lt: String(aggregate.range.lt),
                        aggregateOnFrequencyType:
                          trendChartBinSizeMs ===
                          TRENDS_BIN_SIZE_VALUES_MS_MAP.hours
                            ? "HOURLY"
                            : trendChartBinSizeMs ===
                                TRENDS_BIN_SIZE_VALUES_MS_MAP.days
                              ? "DAILY"
                              : trendChartBinSizeMs ===
                                  TRENDS_BIN_SIZE_VALUES_MS_MAP.weeks
                                ? "WEEKLY"
                                : // SHIFTLY, MONTHLY
                                  "",
                      },
                    }),
                  },
                ],
                [] as { name: string; url: string }[]
              ),
            },
          },
        },
      })["Askr"] as unknown as NetworkConnectionType,
    [
      shifts,
      segments,
      currentAskrSegment,
      allTimesUpdated,
      askrRequests,
      allRollupsMetricTrendChartBarsArrays,
      rollupsMetricByProcess,
      askrGraphiqlUrl,
      trendChartBinSizeMs,
    ]
  );
}

function isNonEmptyObject(object: Record<string, unknown> | null): boolean {
  return Boolean(object && Object.keys(object).length > 0);
}
function isNonEmptyArray(array: any[]): boolean {
  return Boolean(array?.length > 0);
}
function numKeys(object: Record<string, unknown> | null): number {
  return object ? Object.keys(object).length : 0;
}

/** adds a human-readable date range for easier debugging */
function addReadableDateRangeToObject(
  val: DataDumpAggregateType | number | null
) {
  return typeof val === "object" && val?.range?.ge
    ? {
        geReadable: new Date(val.range.ge),
        ltReadable: new Date(val.range.lt),
        ...val,
      }
    : val;
}

function getQueryBody(doc: DocumentNode) {
  return (doc.loc && doc.loc.source.body) ?? "";
}

function getGqlUrl({
  base,
  queryBody,
  variables,
}: {
  base: string;
  queryBody: string;
  variables: Record<string, unknown>;
}) {
  const isGraphqlPlayground = base.includes("askr");
  return `${base}${isGraphqlPlayground ? "?" : "#"}query=${encodeURIComponent(
    queryBody
  )}&variables=${encodeURI(JSON.stringify(variables))}`;
}
